import * as angular from 'angular';
import * as _ from 'lodash';

import { toDictionary } from '../../common/helpers/valueViewModelHelper';
import {
    IValueViewModel,
    Group,
    GroupDto,
    IBindable,
    AlarmList,
    Alarm,
    ConversionDTO,
    ConvertedValuesByType,
    ConvertedValue,
    Unit,
    Dashboard,
    Dictionary
} from '../../models';
import {
    ITagsTrendService,
    IwidgetService,
    IIdentifierVmService,
    IChartByIdentifierService,
    IFlowChartService,
    IGroupService,
    ISeriesBuilder,
    SeriesDictionary,
    IBusinessKpiService,
    ITableWidgetService,
    ISeriesService,
    IBindingResult,
    IGroupStatesWidgetService,
    SeriesMappingResult,
    ILoggerService
} from '../';
import { IWidget, SeriesConfiguration } from '../../widgets';
import { ITileWidgetService, IAlarmService, IConversionService, IVariableService, IDashboardService } from '../../services/data';
import { ILogbookTableService } from './logbookTable.service';
import { IApprovalTableService } from './approvalTable.service';
import { TagsTrendWidget } from '../../widgets/widgets.index';
import { guidEmpty } from '../../common/';
import { IPivotTableService } from './pivotTable.service';

export interface IDashboardSeriesService {
    getSeries(widgets: IWidget[], fromGroup?: Group, toGroup?: Group): angular.IPromise<SeriesDictionary>;
    mapSeries(
        widgets: IWidget[],
        vms: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        alarms: AlarmList,
        fromGroup?: Group,
        toGroup?: Group
    ): angular.IPromise<SeriesDictionary>;
    mapPromise(
        w: IWidget,
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        mapped: IBindingResult,
        guids: string[],
        alarms?: AlarmList
    ): angular.IPromise<any | null>;
}

export class DashboardSeriesService implements IDashboardSeriesService {
    private _strategies: { [type: string]: ISeriesBuilder };

    static serviceId = 'dashboardSeriesService';
    static $inject = [
        '$q',
        'tagsTrendService',
        'chartByIdentifierService',
        'flowChartService',
        'businessKpiService',
        'tableWidgetService',
        'groupStatesWidgetService',
        'tileWidgetService',
        'logbookTableService',
        'approvalTableService',
        'pivotTableService',
        'widgetService',
        'identifierVmService',
        'seriesService',
        'groupService',
        'alarmService',
        'conversionService',
        'variableService',
        'dashboardService',
        'loggerService'
    ];

    constructor(
        private _$q: angular.IQService,
        tts: ITagsTrendService,
        cbis: IChartByIdentifierService,
        fcs: IFlowChartService,
        bks: IBusinessKpiService,
        ts: ITableWidgetService,
        gsws: IGroupStatesWidgetService,
        tws: ITileWidgetService,
        lts: ILogbookTableService,
        ats: IApprovalTableService,
        pts: IPivotTableService,
        private _widgetService: IwidgetService,
        private _identifierVmService: IIdentifierVmService,
        private _seriesService: ISeriesService,
        private _groupService: IGroupService,
        private _alarmsService: IAlarmService,
        private _conversionService: IConversionService,
        private _variableService: IVariableService,
        private _dashboardService: IDashboardService,
        private _loggerService: ILoggerService
    ) {
        this._strategies = {
            tagsTrend: tts,
            scatter: tts,
            chartByIdentifier: cbis,
            DoughnutChart: cbis,
            FlowChart: fcs,
            businessKpi: bks,
            table: ts,
            GroupStates: gsws,
            Tile: tws,
            LogbookTable: lts,
            approvalTable: ats,
            gaugeChart: cbis,
            pivotTable: pts
        };
    }

    getSeries(widgets: IWidget[], fromGroup: Group = null, toGroup: Group = null): angular.IPromise<SeriesDictionary> {
        const promises = [
            this._identifierVmService.getAllViewModels(),
            this._groupService.getAll(),
            this._alarmsService.getAll(),
            this._dashboardService.getAll(false, true)
        ];
        return this._$q.all(promises).then((responses) => {
            const vms = toDictionary(responses[0] as IValueViewModel[]);
            const groups = toDictionary((responses[1] as GroupDto[]).map((g) => g.group));
            const alarms = responses[2] as AlarmList;
            const dashboards = toDictionary(responses[3] as Dashboard[]);
            return this.mapSeries(widgets, vms, groups, dashboards, alarms, fromGroup, toGroup);
        });
    }

    mapSeries(
        widgets: IWidget[],
        vms: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        alarms: AlarmList,
        fromGroup: Group = null,
        toGroup: Group = null
    ) {
        const promises: { [widgetGuid: string]: angular.IPromise<any> } = {};
        widgets.forEach((w) => {
            const p = this.getPromise(w, fromGroup, toGroup, vms, groups, dashboards, alarms);
            if (p) {
                promises[w.guid] = p;
            }
        });
        return (
            this._$q
                .all(promises)
                // Convert alarms' limits through ConversionController in case of unit conversion
                .then((responses) => this.mapAndConvert(widgets, responses, vms))
        );
    }

    private getPromise(
        w: IWidget,
        fromGroup: Group,
        toGroup: Group,
        vms: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        alarms: AlarmList
    ): angular.IPromise<SeriesMappingResult<any> | null> {
        let promise: angular.IPromise<SeriesMappingResult<any> | null> = null;
        const series = this._widgetService.getSeries(w) as SeriesConfiguration[];
        if (series) {
            try {
                let guids: string[] = series.map((s) => s.guid);
                const mapResult = this.computeMapping(fromGroup, toGroup, series, vms, groups);
                if (mapResult.guids) {
                    guids = mapResult.guids;
                }
                const { mapped } = mapResult;
                if (w.extraSeries) {
                    // extra series for widget , such as shapes...
                    try {
                        const secondaryMapping = this.computeMapping(fromGroup, toGroup, w.extraSeries(), vms, groups);
                        if (secondaryMapping.mapped) {
                            for (const key of Object.keys(secondaryMapping.mapped.map)) {
                                if (!mapped.map[key]) {
                                    mapped.map[key] = secondaryMapping.mapped.map[key];
                                }
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }

                promise = this.mapPromise(w, vms, groups, dashboards, mapped, guids, alarms);
            } catch (error) {
                console.error(error);
            }
        }
        return promise;
    }

    mapAndConvert(widgets: IWidget[], responses: Dictionary<{ series: any[] }>, vms: Dictionary<IValueViewModel>) {
        let data: ConversionDTO = {};
        // Build objects
        const filteredWidgets = widgets.filter(
            (w) =>
                w.type === 'tagsTrend' &&
                (w as TagsTrendWidget).configuration.showAlarms &&
                (w as TagsTrendWidget).configuration.options.variableTypeGuid &&
                (w as TagsTrendWidget).configuration.options.variableTypeGuid !== guidEmpty
        );
        return filteredWidgets.length
            ? //TODO bottleneck
              this._variableService.getAll().then((variables) => {
                  data = this.buildConversionDto(filteredWidgets, responses, vms, variables, data);
                  // Call conversion method
                  return this._conversionService.convert(data).then((converted) => this.reassignAlarms(filteredWidgets, responses, converted));
              })
            : this._$q.when(responses);
    }

    private buildConversionDto(
        widgets: IWidget[],
        responses: Dictionary<{ series: any[] }>,
        vms: Dictionary<IValueViewModel>,
        variables: any[],
        data: ConversionDTO
    ): ConversionDTO {
        const vmIndex: Dictionary<IValueViewModel> = {};
        _.values(vms).forEach((v) => (vmIndex[v.valueType + v.guid] = v));

        widgets.forEach((w: IWidget) => {
            const cvbt: ConvertedValuesByType = {
                convertedValues: {},
                variableTypeGuid: ''
            };

            if (w.guid && responses[w.guid] && responses[w.guid].series) {
                responses[w.guid].series
                    .filter((s) => s.alarms && s.alarms.length)
                    .forEach((serie) => {
                        const viewModelFound = vmIndex[serie.identifier];
                        let unitFrom: Unit;
                        let unitTo: Unit;
                        let isOk = true;
                        if (viewModelFound) {
                            if (viewModelFound.variableTypeGuid === w.configuration.options.variableTypeGuid) {
                                cvbt.variableTypeGuid = viewModelFound.variableTypeGuid;
                                const variableFound = variables.filter((v) => v.variable.guid === cvbt.variableTypeGuid);
                                if (variableFound && variableFound.length === 1) {
                                    const units = variableFound[0].variable.unitCollection;
                                    const unitFromFound = units.filter((u) => u.guid === viewModelFound.unitGuid);
                                    if (unitFromFound && unitFromFound.length === 1) {
                                        unitFrom = unitFromFound[0];
                                    } else {
                                        isOk = false;
                                        this._loggerService.logError(`Source unit isn't valid`);
                                    }
                                    const unitToFound = units.filter((u) => u.guid === w.configuration.options.unitGuid);
                                    if (unitToFound && unitToFound.length === 1) {
                                        unitTo = unitToFound[0];
                                    } else {
                                        isOk = false;
                                        this._loggerService.logError(`Destination unit isn't valid`);
                                    }
                                }
                            } else {
                                isOk = false;
                                // Not an error, just a series that should not be converted
                                // this._loggerService.logError('Variable type guid from widget configuration isn\'t compatible with variable type guid from serie.');
                            }
                        }

                        if (isOk) {
                            serie.alarms.forEach((alarm) => {
                                const baseKey = `alarm_${alarm.guid}`;
                                this.mapConvertedValue(alarm.high, baseKey + '_high', unitFrom, unitTo, cvbt);
                                this.mapConvertedValue(alarm.low, baseKey + '_low', unitFrom, unitTo, cvbt);
                            });
                        }
                    });
            }
            data[w.guid] = cvbt;
        });
        return data;
    }

    private mapConvertedValue(value: number, key: string, unitFrom: Unit, unitTo: Unit, cvbt) {
        if (value || value === 0) {
            const cv: ConvertedValue = {
                originalValue: value,
                value: 0,
                from: unitFrom,
                to: unitTo
            };
            cvbt.convertedValues[key] = cvbt.convertedValues[key] || [];
            cvbt.convertedValues[key].push(cv);
        }
    }

    reassignAlarms(widgets: IWidget[], responses: any, convertedData: ConversionDTO): any {
        if (responses) {
            widgets.forEach((w: IWidget) => {
                if (w.guid && responses[w.guid] && responses[w.guid].series) {
                    responses[w.guid].series
                        .filter((s) => s.alarms && s.alarms.length)
                        .forEach((serie) => {
                            serie.alarms = serie.alarms.map((a) => {
                                const alarm = angular.copy(a);
                                let key: string;
                                const baseKey = `alarm_${alarm.guid}`;
                                if ((alarm.high || alarm.high === 0) && !alarm.isManual) {
                                    key = baseKey + '_high';
                                    if (
                                        convertedData[w.guid] &&
                                        convertedData[w.guid].convertedValues[key] &&
                                        convertedData[w.guid].convertedValues[key].length > 0
                                    ) {
                                        alarm.high = convertedData[w.guid].convertedValues[key][0].value;
                                    }
                                }
                                if ((alarm.low || alarm.low === 0) && !alarm.isManual) {
                                    key = baseKey + '_low';
                                    if (
                                        convertedData[w.guid] &&
                                        convertedData[w.guid].convertedValues[key] &&
                                        convertedData[w.guid].convertedValues[key].length > 0
                                    ) {
                                        alarm.low = convertedData[w.guid].convertedValues[key][0].value;
                                    }
                                }
                                return alarm;
                            });
                        });
                }
            });
        }
        return responses;
    }

    private computeMapping(fromGroup: Group, toGroup: Group, series: IBindable[], vms: Dictionary<IValueViewModel>, groups: Dictionary<Group>) {
        let guids: string[];
        let mapped: IBindingResult;
        if (fromGroup && toGroup && fromGroup.guid !== toGroup.guid) {
            const boundSeries = series.filter((s) => s.isBoundToGroup && !s.isQuery).map((s) => s.guid);
            mapped = this._seriesService.bindSeriesToReference(boundSeries, vms, groups, fromGroup, toGroup);
            guids = Object.keys(mapped.map)
                .filter((k) => mapped.map[k])
                .map((k) => mapped.map[k])
                .concat(series.filter((s) => !s.isBoundToGroup).map((s) => s.guid));
            // precisely don't add errors as we don't want to see them
            //.concat(Object.keys(mapped.errors))
            // TODO something for errors ?
        }
        return { mapped, guids };
    }

    mapPromise(
        w: IWidget,
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        dashboards: Dictionary<Dashboard>,
        mapped: IBindingResult,
        guids: string[],
        alarms: AlarmList = null
    ): angular.IPromise<SeriesMappingResult<any> | null> {
        const vms = toDictionary(guids.map((g) => viewModels[g]).filter((v) => v));
        const seriesBuilder = this._strategies[w.type];
        let result: angular.IPromise<SeriesMappingResult<any>> = null;

        if (seriesBuilder) {
            result = seriesBuilder.buildSeriesFromViewModels({
                configuration: w.configuration,
                dashboards: dashboards,
                groups: groups,
                map: mapped ? mapped.map : null,
                errors: mapped ? mapped.errors : null,
                viewModels: vms
            });
        }

        return result ? result.then((data) => this.remapAlarms(guids, alarms, data)) : result;
    }

    private remapAlarms(guids: string[], alarms: AlarmList, data: SeriesMappingResult<any>): SeriesMappingResult<any> {
        if (alarms) {
            const alDic: { [id: string]: Alarm[] } = {};
            guids.forEach((g) => {
                const a = alarms[g];
                if (a && a.length) {
                    a.forEach((d) => {
                        let o = alDic[d.value + g];
                        if (!o) {
                            o = [];
                            alDic[d.value + g] = o;
                        }
                        o.push(d);
                    });
                }
            });
            data.series.forEach((s) => {
                // TODO
                s.alarms = alDic[s.identifier] || [];
            });
        }
        return data;
    }
}
